import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import cn from 'classnames';
import { ChatAI, X } from 'src/common/icons';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { ICON_MAP } from 'src/chat/icons';
import { useChatSuggestionsQuery } from 'src/api/queries/chatQueries';
import { ASK_OPEN_ID, NUDGE_SURFACE, NudgeSurface, pickStarters } from './starterGates';
import { useAgentPrefill } from './useAgentPrefill';
import { useStarterGates } from './useStarterGates';
import messages from './messages';
import styles from './AgentNudgeRow.module.scss';

const CHIP_LIMIT = { desktop: 6, mobile: 3, modal: 3 } as const;

export type AgentNudgeRowProps = {
  surface: NudgeSurface;
  /** What "the selected assets" must resolve to when a chip is clicked. */
  assetIds: string[];
  /** Same order as assetIds; '' for unknown. The server picks image/video
   *  chips from these. */
  contentTypes: string[];
  /**
   * Point the agent's selection context at `assetIds` on click. Used by the
   * asset-detail and post-upload surfaces, where the user selected nothing.
   */
  claimSelection?: boolean;
  /** Runs after a chip is applied — the asset modal closes itself here. */
  onActivate?: () => void;
  /** Post-upload only: renders a dismiss control. */
  onDismiss?: () => void;
  className?: string;
};

/**
 * A row of ready-made agent prompts, shown at the moment a user has picked
 * assets (or just uploaded them). Clicking a chip pre-fills the agent panel;
 * nothing is ever sent without the user pressing Enter.
 *
 * The chips come from the same server catalogue as the chat empty state
 * (GET /api/v1/chat/suggestions). Renders nothing at all unless the agent is
 * usable, the account has credits, settings have hydrated, and at least one
 * starter survives its gates — see `useStarterGates`.
 */
export const AgentNudgeRow: React.FC<AgentNudgeRowProps> = ({
  surface,
  assetIds,
  contentTypes,
  claimSelection,
  onActivate,
  onDismiss,
  className,
}) => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const prefill = useAgentPrefill();
  const { agentReady, hydrated, ctx } = useStarterGates();
  const enabled = agentReady && hydrated && assetIds.length > 0;
  const { data } = useChatSuggestionsQuery(
    {
      surface: NUDGE_SURFACE[surface],
      selected: assetIds.length,
      assetTypes: contentTypes,
    },
    { enabled },
  );

  const limit = isMobile
    ? CHIP_LIMIT.mobile
    : surface === 'asset-detail'
    ? CHIP_LIMIT.modal
    : CHIP_LIMIT.desktop;

  const chips = useMemo(
    // Nudges prefill text, so a UI-action starter has no place here.
    () => (enabled ? pickStarters(data?.starters?.filter((s) => s.message), ctx, limit) : []),
    [enabled, data, ctx, limit],
  );

  if (!enabled || chips.length === 0) return null;

  const apply = (id: string, label: string, prompt: string) =>
    prefill(label, prompt, {
      selectAssetIds: claimSelection ? assetIds : undefined,
      nudgeId: id,
      after: onActivate,
    });

  return (
    <div
      className={cn(styles.row, { [styles.inModal]: surface === 'asset-detail' }, className)}
      role="group"
      aria-label={formatMessage(messages.groupLabel)}
      data-test="agent-nudge-row"
    >
      <span className={styles.lead} aria-hidden>
        <ChatAI />
        {formatMessage(messages.lead)}
      </span>

      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          className={styles.chip}
          onClick={() => apply(chip.id, chip.label, chip.message ?? '')}
          title={chip.message}
          data-test={`agent-nudge-${chip.id}`}
        >
          {chip.icon && ICON_MAP[chip.icon]}
          {chip.label}
        </button>
      ))}

      {/* Escape hatch for intents that aren't in the catalog: opens the panel
          with the selection in context but no draft. */}
      <button
        type="button"
        className={cn(styles.chip, styles.chipOpen)}
        onClick={() =>
          prefill('', '', {
            selectAssetIds: claimSelection ? assetIds : undefined,
            nudgeId: ASK_OPEN_ID,
            after: onActivate,
          })
        }
        data-test="agent-nudge-open"
      >
        {formatMessage(messages.askOpen)}
      </button>

      {onDismiss && (
        <button
          type="button"
          className={styles.dismiss}
          onClick={onDismiss}
          aria-label={formatMessage(messages.dismiss)}
          data-test="agent-nudge-dismiss"
        >
          <X />
        </button>
      )}
    </div>
  );
};

export default AgentNudgeRow;
